import React, { use, useEffect, useRef, useState } from "react";
import moment from "moment";
import * as XLSX from "xlsx";
import SectionHeader from "../components/SectionHeader";
import {
  getDocketEntries,
  update,
  updateDocketEntry,
} from "../services/docketEntryService";
import DocketForm from "../components/DocketForm";
import { toast } from "react-toastify";
import DocketPreview from "../components/DockerPreview";
import { printDocket } from "../utils/printDocket";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  CircleX,
  EyeIcon,
  FileSpreadsheet,
  PencilIcon,
  PrinterIcon,
} from "lucide-react";
import { getBranches } from "../services/branchService";
import { useSelector } from "react-redux";

const DocketList = () => {
  const [selectedDate, setSelectedDate] = React.useState(
    new Date().toISOString().split("T")[0],
  );
  const today = new Date().toISOString().split("T")[0];
  const { user } = useSelector((state) => state.user);
  const previewRef = useRef();
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [selectedBranch, setSelectedBranch] = useState("");
  const printRef = useRef();
  const [showDocketEdit, setShowDocketEdit] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [docketList, setDocketList] = React.useState([]);
  const [formData, setFormData] = React.useState({});
  const [showDocketPreview, setShowDocketPreview] = React.useState(false);
  const [branches, setBranches] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDockets, setTotalDockets] = useState(0);
  const [isExporting, setIsExporting] = useState(false);

  const goToPrevDate = () => {
    const currentDate = new Date(selectedDate);
    currentDate.setDate(currentDate.getDate() - 1);
    setSelectedDate(currentDate.toISOString().split("T")[0]);
  };

  const goToNextDate = () => {
    const currentDate = new Date(selectedDate);
    currentDate.setDate(currentDate.getDate() + 1);
    setSelectedDate(currentDate.toISOString().split("T")[0]);
  };
  const handlePrintList = () => {
    const printContents = printRef.current.innerHTML;
    const originalContents = document.body.innerHTML;

    document.body.innerHTML = printContents;
    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload();
  };
  const handlePrintPreview = () => printDocket(formData);

  const handleExportData = async () => {
    if (isExporting || totalDockets === 0) return;
    setIsExporting(true);
    try {
      // Omit page/limit so the API returns the full filtered dataset, not just the current page
      const params = {
        branchCode: selectedBranch || undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        search: search || undefined,
      };
      const response = await getDocketEntries(params);
      const allDockets = Array.isArray(response?.data)
        ? response.data
        : response?.data?.dockets || [];

      if (allDockets.length === 0) {
        toast.error("No data available to export");
        return;
      }

      const exportRows = allDockets.map((docket) => ({
        "Docket Id": docket?.docketNumber ?? "-",
        Date: formatDate(docket?.bookingDate),
        Time: formatTime(docket?.bookingTime),
        "No of Box": docket?.numberOfBoxes ?? "-",
        "Actual Wt (Kg)": docket?.actualWeight ?? "-",
        "Charged Wt (Kg)": docket?.chargedWeight ?? "-",
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Docket List");
      XLSX.writeFile(
        workbook,
        `Docket_List_${moment().format("YYYY-MM-DD")}.xlsx`,
      );
    } catch (error) {
      console.error("Error exporting docket list:", error);
      toast.error("Failed to export docket list");
    } finally {
      setIsExporting(false);
    }
  };

  const handleGetDocketList = async () => {
    try {
      const params = {
        page,
        limit,
        branchCode: selectedBranch,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        search: search || undefined,
      };
      const response = await getDocketEntries(params);
      if (response?.status === 200) {
        setDocketList(response?.data?.dockets || []);
        setTotalPages(response?.data?.totalPages || 1);
        setPage(response?.data?.currentPage || 1);
        setTotalDockets(response?.data?.totalDockets || 0);
      } else {
        console.error("Failed to fetch docket list:", response.statusText);
      }
    } catch (error) {
      console.error("Error fetching docket list:", error);
    }
  };
  const handleGetBranches = async () => {
    try {
      const response = await getBranches();
      if (response?.status === 200) {
        setBranches(response?.data);
      } else {
        console.error("Failed to fetch branches:", response.statusText);
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };
  const handleDocketSave = async (docket) => {
    try {
      const res = await updateDocketEntry(docket._id, docket);
      if (res?.status === 200) {
        console.log("Docket saved successfully:", res);
        handleGetDocketList();
        toast.success("Docket saved successfully");
        setShowDocketEdit(false);
      }
    } catch (error) {
      console.error("Error saving docket:", error);
      toast.error(error?.response?.data?.error);
    }
  };
  useEffect(() => {
    // handleGetDocketList();
    handleGetBranches();
    if (user?.role === "EMPLOYEE") {
      setSelectedBranch(user?.branch?.code || "");
    }
  }, [user]);
  useEffect(() => {
    // Add a simple debounce for search
    const delayDebounceFn = setTimeout(() => {
      if (selectedBranch) {
        handleGetDocketList();
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [selectedBranch, page, limit, fromDate, toDate, search]);

  const selectedBranchName =
    branches.find((b) => b._id === selectedBranch)?.branchName ||
    "All Branches";

  const formatTime = (timeStr) => {
    if (!timeStr) return "-";
    return moment(timeStr, ["HH:mm:ss", "HH:mm", "hh:mm A", "h:mm A"]).format(
      "hh:mm A",
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return moment(dateStr).format("DD-MM-YYYY");
  };

  return (
    <div className="p-3 bg-slate-200 min-h-full">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-2">
        <SectionHeader
          title="Docket List"
          subTitle="Manage and track all dockets"
        />

        <div className="flex items-center gap-2 w-full md:w-auto">
          {/* Date */}
          <div className="flex items-center gap-2 bg-white border rounded-lg px-3 py-1">
            {/* FROM DATE */}
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-500">From</span>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setPage(1);
                }}
                className="px-2 py-1 text-sm bg-transparent outline-none"
              />
            </div>

            {/* Separator */}
            <span className="text-gray-400">→</span>

            {/* TO DATE */}
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-500">To</span>
              <input
                type="date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setPage(1);
                }}
                className="px-2 py-1 text-sm bg-transparent outline-none"
              />
            </div>
          </div>
          {/* Branch DrodDown */}
          <div>
            <select
              value={selectedBranch}
              onChange={(e) => {
                setSelectedBranch(e.target.value);
                setPage(1);
              }}
              name="branchId"
              className="px-4 py-2 border rounded-md text-sm bg-white"
            >
              <option value="">Select Branch</option>
              {branches?.map((b) => (
                <option key={b._id} value={b.branchCode}>
                  {b.branchName}
                </option>
              ))}
            </select>
          </div>
          {/* Search */}
          <input
            type="text"
            placeholder="Search pickup, customer, address…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="md:w-72 px-4 py-2 border rounded-lg text-sm"
          />
          <button
            disabled={showDocketEdit}
            onClick={handlePrintList}
            className="py-2 bg-white px-6 cursor-pointer rounded-lg hover:bg-sky-50 transition"
          >
            <PrinterIcon size={20} color={showDocketEdit ? "gray" : "black"} />
          </button>
          <button
            disabled={showDocketEdit || isExporting || totalDockets === 0}
            onClick={handleExportData}
            title={
              totalDockets === 0 ? "No data available to export" : "Export Data"
            }
            className="py-2 bg-white px-4 cursor-pointer rounded-lg hover:bg-sky-50 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium"
          >
            <FileSpreadsheet
              size={20}
              color={
                showDocketEdit || isExporting || totalDockets === 0
                  ? "gray"
                  : "black"
              }
            />
            {/* {isExporting ? "Exporting..." : "Export Data"} */}
          </button>
        </div>
      </div>
      {/* Docket Table - To be implemented */}
      {/* Table */}
      {showDocketEdit ? (
        <div>
          <div className="flex justify-between">
            <button
              onClick={() => setShowDocketEdit(false)}
              className="px-3 py-2 mb-2 bg-gray-600 text-white rounded text-xs"
            >
              Back to Docket List
            </button>
            <div className="flex gap-3">
              <button
                onClick={() => handleDocketSave(formData)}
                className="px-3 py-2 mb-2 bg-gray-600 text-white rounded text-xs"
              >
                Save
              </button>
              <button
                onClick={() => setShowDocketPreview(!showDocketPreview)}
                className="px-3 py-2 mb-2 bg-gray-600 text-white rounded text-xs"
              >
                Preview
              </button>
            </div>
          </div>

          <DocketForm formData={formData} setFormData={setFormData} />
        </div>
      ) : (
        <div
          ref={printRef}
          className="bg-white border rounded-xl overflow-hidden"
        >
          <div className="hidden print:block pt-2 py-4">
            <div className="flex justify-between text-sm">
              <div className="">
                <h2 className="text-lg font-bold mb-1">Docket List</h2>
                <p>
                  <strong>Branch:</strong> {selectedBranchName}
                </p>
                {/* <p>
                  <strong>Date:</strong> {selectedDate}
                </p> */}
              </div>
              <div className="text-right">
                <p>
                  <strong>Date:</strong>
                </p>
                <p>{selectedDate}</p>
              </div>
            </div>
          </div>
          {/* Responsive wrapper: allows horizontal scroll on small screens */}
          <div className="overflow-x-auto">
            <table className="min-w-full border-2">
              <thead className="bg-gray-100 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-3 text-center">Docket Id</th>
                  <th className="px-4 py-3 border-l">Date</th>
                  <th className="px-4 py-3 border-l">Time</th>
                  <th className="px-4 py-3 border-l ">No of Box</th>
                  <th className="px-4 py-3 border-l text-center">
                    Actual Wt (Kg)
                  </th>
                  <th className="px-4 py-3 border-l text-center ">
                    Charged Wt (Kg)
                  </th>
                  <th className="px-4 py-3 border-l text-center print:hidden">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody>
                {docketList?.map((docket) => (
                  <tr key={docket._id} className="border-t text-sm">
                    <td className="px-4 py-3 border-l font-medium text-center ">
                      {docket?.docketNumber}
                    </td>
                    <td className="px-4 py-3 border-l text-center ">
                      {formatDate(docket?.bookingDate)}
                    </td>
                    <td className="px-4 py-3 border-l text-center  ">
                      {formatTime(docket.bookingTime)}
                    </td>
                    <td className="px-4 py-3 border-l text-center ">
                      {docket?.numberOfBoxes}
                    </td>
                    <td className="px-4 py-3 border-l text-center ">
                      {docket?.actualWeight}
                    </td>
                    <td className="px-4 py-3 border-l text-center ">
                      {docket?.chargedWeight}
                    </td>

                    {/* <td className="px-4 py-3 border-l text-center print:hidden">
                      <button
                        onClick={() => {
                          setShowDocketEdit(true);
                          setFormData(docket);
                        }}
                        className="px-3 py-1 bg-gray-600 text-white rounded text-xs"
                      >
                        Edit
                      </button>
                    </td> */}
                    <td className="px-4 py-3 border-l flex items-center justify-center gap-2 text-center print:hidden">
                      <button
                        onClick={() => {
                          setShowDocketEdit(true);
                          setFormData(docket);
                        }}
                        className="px-3 py-1 bg-gray-600 text-white rounded text-xs"
                      >
                        <PencilIcon size={18} color="white" />
                      </button>
                      <button
                        onClick={() => {
                          setFormData(docket);
                          setShowDocketPreview(true);
                        }}
                        className="px-3 py-1 bg-gray-600 text-white rounded text-xs"
                      >
                        <EyeIcon size={18} color="white" />
                      </button>
                    </td>
                  </tr>
                ))}
                {docketList?.length === 0 && (
                  <tr className="h-80">
                    <td colSpan="8" className="py-6 text-center text-gray-500">
                      No dockets for selected date
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {/* Pagination Controls */}
          <div className="flex items-center justify-between p-4 border-t bg-gray-50 print:hidden">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Rows per page:</span>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
                className="border rounded px-2 py-1 text-sm bg-white"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            <div className="flex items-center gap-4 text-sm">
              <span className="text-gray-600">
                Page {page} of {totalPages || 1}
              </span>
              <div className="flex gap-1">
                <button
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  disabled={page >= totalPages || totalPages === 0}
                  onClick={() => setPage(page + 1)}
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <modal
        isOpen={showDocketPreview}
        onClose={() => setShowDocketPreview(false)}
      >
        {showDocketPreview && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            {/* MODAL BOX */}
            <div className="relative w-full max-w-6xl max-h-[92vh] bg-white rounded-2xl shadow-2xl overflow-hidden">
              {/* HEADER */}
              <div className="flex items-center justify-between px-2 py-2 border-b bg-white print:hidden">
                <h2 className="text-lg font-bold">Docket Preview</h2>
                <div className="flex gap-2 items-center">
                  <button
                    onClick={handlePrintPreview}
                    className="px-3 py-2  bg-gray-600 text-white rounded text-xs print:hidden"
                  >
                    <PrinterIcon size={18} color="white" />
                  </button>
                  <button
                    onClick={() => setShowDocketPreview(false)}
                    className="px-3 py-2  bg-gray-600 text-white rounded text-xs print:hidden"
                  >
                    <CircleX size={18} color="white" />
                  </button>
                </div>
              </div>

              {/* CONTENT */}
              <div className="overflow-y-auto bg-gray-100 p-6 max-h-[calc(92vh-64px)] print:bg-white print:p-0">
                <div
                  className="bg-white rounded-xl shadow-md p-6 print:shadow-none print:rounded-none"
                  ref={previewRef}
                >
                  <DocketPreview formData={formData} />
                </div>
              </div>
            </div>
          </div>
        )}
      </modal>
    </div>
  );
};

export default DocketList;
